import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from "react";
import { FormGroup } from "react-bootstrap";
import { FiSearch, FiCheck } from "react-icons/fi";
import { HiOutlineChevronDown } from "react-icons/hi2";
import "./PlanFunctionsMultiSelect.scss";

function normId(id) {
	if (id === null || id === undefined) return null;
	const s = String(id).trim();
	if (s === "" || s === "-1") return null;
	return s;
}

function normalizeIds(value) {
	if (!Array.isArray(value)) return [];
	return value.map(normId).filter(Boolean);
}

function formatCountLabel(label, count) {
	if (!label) return "";
	return String(label).replace("{{count}}", String(count));
}

/**
 * Multi-selector de funciones para filtrar una planificación.
 *
 * @param {Array}    functions   — lista de funciones (`{ id, name }`).
 * @param {Array}    value       — ids seleccionados (array de strings/numbers).
 * @param {function} onChange    — `(ids: string[]) => void` cuando cambia la selección.
 * @param {string}   [placeholder]
 * @param {string}   [allLabel]
 * @param {string}   [searchPlaceholder]
 * @param {string}   [clearLabel]
 * @param {string}   [noResultsLabel]
 * @param {string}   [selectedCountLabel]
 * @param {string}   [hint]
 * @param {string}   [variant]   — `default` (filtro compacto) o `panel` (formulario).
 * @param {string}   [className]
 * @param {boolean}  [disabled]
 * @param {string}   [controlId]
 */
const PlanFunctionsMultiSelect = ({
	functions = [],
	value,
	onChange,
	placeholder = "Filtrar por función",
	allLabel = "Todas las funciones",
	searchPlaceholder = "Buscar función…",
	clearLabel = "Limpiar selección",
	noResultsLabel = "No se encontraron funciones",
	selectedCountLabel = "{{count}} funciones seleccionadas",
	hint,
	variant = "default",
	className = "",
	disabled = false,
	controlId,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const dropdownRef = useRef(null);
	const isPanel = variant === "panel";

	const selectedIds = useMemo(() => normalizeIds(value), [value]);

	const functionsById = useMemo(() => {
		const map = new Map();
		(functions || []).forEach((fn) => {
			if (fn && fn.id !== undefined && fn.id !== null) {
				map.set(String(fn.id), fn);
			}
		});
		return map;
	}, [functions]);

	const selectedObjects = useMemo(() => {
		return selectedIds.map((id) => functionsById.get(id)).filter(Boolean);
	}, [selectedIds, functionsById]);

	const filteredFunctions = useMemo(() => {
		const q = searchInput.trim().toLowerCase();
		if (!q) return functions || [];
		return (functions || []).filter((fn) =>
			(fn.name || "").toLowerCase().includes(q)
		);
	}, [functions, searchInput]);

	useEffect(() => {
		const onDoc = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchInput("");
			}
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, []);

	const triggerLabel = useMemo(() => {
		if (selectedIds.length === 0) {
			return <span className="placeholder">{placeholder || allLabel}</span>;
		}
		if (isPanel) {
			return (
				<span className="selected-item">
					{selectedObjects.map((fn) => fn.name).join(", ")}
				</span>
			);
		}
		if (selectedObjects.length === 1) {
			return (
				<span className="selected-item">{selectedObjects[0].name}</span>
			);
		}
		return (
			<span className="selected-count">
				{formatCountLabel(selectedCountLabel, selectedIds.length)}
			</span>
		);
	}, [
		selectedIds,
		selectedObjects,
		placeholder,
		allLabel,
		selectedCountLabel,
		isPanel,
	]);

	const handleToggleOpen = () => {
		if (disabled) return;
		setIsOpen((o) => !o);
		if (isOpen) setSearchInput("");
	};

	const handleToggleFunction = useCallback(
		(fn) => {
			const idStr = String(fn.id);
			const set = new Set(selectedIds);
			if (set.has(idStr)) set.delete(idStr);
			else set.add(idStr);
			onChange(Array.from(set));
		},
		[selectedIds, onChange]
	);

	const handleClear = (event) => {
		if (event) event.stopPropagation();
		onChange([]);
	};

	const isSelected = (id) => selectedIds.includes(String(id));

	const renderSearch = () => (
		<div className={isPanel ? "search-wrap" : undefined}>
			<input
				type="text"
				placeholder={searchPlaceholder}
				value={searchInput}
				onChange={(e) => setSearchInput(e.target.value)}
				className="search-input"
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.preventDefault();
				}}
				autoFocus
			/>
			{isPanel && <FiSearch aria-hidden="true" />}
		</div>
	);

	return (
		<FormGroup
			className={`plan-functions-multiselect ${
				isPanel ? "plan-functions-multiselect--panel" : ""
			} ${className}`}
			style={isPanel ? undefined : { position: "relative" }}
			controlId={controlId}
		>
			<div
				className={`custom-select-container ${isOpen ? "is-open" : ""}`}
				ref={dropdownRef}
			>
				<div
					className={`custom-select select-control ${isOpen ? "open" : ""} ${
						disabled ? "disabled" : ""
					}`}
					onClick={handleToggleOpen}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							handleToggleOpen();
						}
					}}
					role="button"
					tabIndex={disabled ? -1 : 0}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
				>
					<div className="selected-items">{triggerLabel}</div>
					<HiOutlineChevronDown
						className="select-trailing-icon"
						size={18}
						aria-hidden="true"
					/>
				</div>

				{isOpen && (
					<div className="dropdown-menu">
						{isPanel ? (
							<div className="dropdown-header">{renderSearch()}</div>
						) : (
							<div className="dropdown-header">
								{selectedIds.length > 0 && (
									<button
										type="button"
										className="clear-all-btn"
										onClick={handleClear}
									>
										{clearLabel}
									</button>
								)}
								{renderSearch()}
							</div>
						)}

						<div className="dropdown-options" role="listbox" aria-multiselectable="true">
							{filteredFunctions.map((fn) => {
								const sel = isSelected(fn.id);
								return (
									<div
										key={fn.id}
										className={`dropdown-option ${sel ? "selected" : ""}`}
										onClick={() => handleToggleFunction(fn)}
										role="option"
										aria-selected={sel}
									>
										{isPanel && (
											<span
												className={`option-checkbox ${sel ? "checked" : ""}`}
												aria-hidden="true"
											>
												{sel ? <FiCheck /> : null}
											</span>
										)}
										<div className="option-content">
											<span className="function-name">{fn.name}</span>
										</div>
										{!isPanel && sel ? (
											<div className="checkmark">✓</div>
										) : null}
									</div>
								);
							})}

							{filteredFunctions.length === 0 && (
								<div className="no-results">{noResultsLabel}</div>
							)}
						</div>

						{isPanel && (
							<div className="dropdown-footer">
								<span className="dropdown-footer__count">
									{formatCountLabel(selectedCountLabel, selectedIds.length)}
								</span>
								{selectedIds.length > 0 && (
									<button
										type="button"
										className="clear-all-btn"
										onClick={handleClear}
									>
										{clearLabel}
									</button>
								)}
							</div>
						)}
					</div>
				)}
			</div>
			{isPanel && hint && <p className="plan-copy-hint">{hint}</p>}
		</FormGroup>
	);
};

export default PlanFunctionsMultiSelect;
